/**
*
* FormBuilderView
*
*/

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ErrorSummary from 'components/ErrorSummary';
import FormFieldAttributes from 'components/Fields';
import MinimizedField from 'components/Fields/minimized.field';
import Modal from 'components/Modal';
import StepBar from 'components/StepBar';
import { ALL_BRANCHES } from 'utils/conditionalConstants';
import FormDetails from './FormDetails';
import FieldInserter from './FieldInserters';

const RENDER_TYPES = {
  MINIMIZED_FIELD: 'MINIMIZED_FIELD',
  STANDARD_FIELD: 'STANDARD_FIELD',
  STEP: 'STEP',
};
export const DELETE_STEP_MODAL_TEXT = 'If you delete this step, all branched questions on this step will lose their branch assignments. \rYou may have to delete duplicate questions in order to save the form.';

function FormBuilderView(props) {
  const availablePrograms = props.availablePrograms || [];
  const availableProgramsOfStudy = props.availableProgramsOfStudy || [];
  const form = props.form || {};
  const invalidFormData = props.invalidFormData || {};
  const loadInProgress = props.loadInProgress ?? false;
  const [showStepDeleteModal, setShowStepDeleteModal] = useState(false);

  const onClickHandler = () => {
    props.fieldClicked(-1);
  };

  const getBranchedFieldsForStep = (stepIndex) => {
    const branchedFields = [];
    let searchIndex = stepIndex + 1;
    const branchingQuestionId = Object.keys(form.fieldConditionalityInfo.branchingFields)[0];
    while (form.fieldOrder[searchIndex]) {
      const fieldOrScreenId = form.fieldOrder[searchIndex];
      if (!form.fields[fieldOrScreenId]) {
        break; // Found next step marker
      }
      if (form.fieldConditionalityInfo.graph.getNearestBranchVerticesForFieldVertex({ left: fieldOrScreenId }, branchingQuestionId).length) {
        branchedFields.push(fieldOrScreenId);
      }
      searchIndex++;
    }
    return branchedFields;
  };

  const createOnClickDeleteStep = (followsBranchingStep, stepIndex) => (stepId) => {
    if (!followsBranchingStep) {
      props.deleteStep(stepId);
    } else {
      const branchedFields = getBranchedFieldsForStep(stepIndex);
      if (branchedFields.length) {
        setShowStepDeleteModal((previousShowStepDeleteModal) => !previousShowStepDeleteModal);
      } else {
        props.deleteStep(stepId);
      }
    }
  };

  const stepOnCancelDelete = () => {
    setShowStepDeleteModal(false);
  };

  const createOnConfirmDeleteStep = (stepId, stepIndex) => () => {
    props.deleteStep(stepId);
    getBranchedFieldsForStep(stepIndex).forEach((fieldId) => {
      props.removeAllUserBranchMembership(fieldId);
    });
    setShowStepDeleteModal(false);
  };

  const determineFieldType = (fieldOrScreenId) => {
    if (form.fields[fieldOrScreenId]) {
      if (props.activeBranchingFieldDetails[0] && props.uiState.visibleBranches !== ALL_BRANCHES) {
        const { id } = props.activeBranchingFieldDetails[0];
        const branchMembership = form.fieldConditionalityInfo.graph.getNearestBranchVerticesForFieldVertex({ left: fieldOrScreenId }, id);
        if (branchMembership.length && !branchMembership.includes(props.uiState.visibleBranches)) {
          return { type: RENDER_TYPES.MINIMIZED_FIELD, details: { branchMembership, branchingQuestion: id } };
        }
      }

      return { type: RENDER_TYPES.STANDARD_FIELD };
    }

    return { type: RENDER_TYPES.STEP };
  };

  const makeFieldOrScreen = (fieldOrScreenId, index, stepMemo, fieldErrors) => {
    const { type, details } = determineFieldType(fieldOrScreenId);
    const obj = form.fields[fieldOrScreenId] || {};

    switch (type) {
      case RENDER_TYPES.MINIMIZED_FIELD: {
        const branchIds = details.branchMembership.map((branchVal) => props.activeBranches[details.branchingQuestion]
          .find((branch) => branch.value === branchVal)
        ).map(({ index: branchIdx }) => branchIdx + 1).sort((a, b) => a - b);

        return (
          <MinimizedField
            branchIds={branchIds}
            key={obj.id}
            title={obj.label}
          />
        );
      }

      case RENDER_TYPES.STANDARD_FIELD: {
        const { formMetadata } = form;
        const { uiState } = props;
        const objUiState = uiState.fields ?
          { ...uiState.fields[obj.id], isCollapsed: uiState.fieldOrderCollapsedState[index] } :
          {};
        objUiState.isActive = uiState.activeFieldId === obj.id;
        const isInferredField = Object.hasOwnProperty.call((form.syntheticContentEntities.inferredFields || {}), obj.name);
        return (
          <FormFieldAttributes
            availableCredentialTypes={props.availableCredentialTypes}
            areAllFieldsOptional={formMetadata && formMetadata.areAllFieldsOptional}
            invalidFormData={invalidFormData}
            activeBranches={props.activeBranches}
            activeBranchingFieldDetails={props.activeBranchingFieldDetails}
            availablePrograms={availablePrograms}
            bulkToggleDropdownOptions={props.bulkToggleDropdownOptions}
            errors={fieldErrors[obj.id]}
            fieldClicked={props.fieldClicked}
            fieldNameDropdownOpts={props.fieldNameDropdownOpts}
            fieldValidityChanged={props.fieldValidityChanged}
            fieldConditionalityInfo={form.fieldConditionalityInfo}
            gdprSetting={form.syntheticContentEntities.gdprSetting}
            thankYouRoutingFields={form.thankYouRoutingFields}
            index={index}
            isInferredField={isInferredField}
            key={obj.id}
            keyPath={['fields', obj.id]}
            loadInProgress={loadInProgress}
            onFieldUIStateChange={props.updateUIState}
            onFieldNameChange={props.updateFieldName}
            onFieldValueChange={props.updateFormValue}
            onDeleteField={props.deleteField}
            removeAllUserBranchMembership={props.removeAllUserBranchMembership}
            reorderField={props.reorderField}
            reorderDropdown={props.reorderDropdown}
            setFieldInferredState={props.setFieldInferredState}
            supportsDegreeOffering={props.supportsDegreeOffering}
            sfFieldData={props.sfFieldData}
            step={stepMemo.index}
            title={obj.label}
            toggleBranching={props.toggleBranching}
            toggleThankYouRoute={props.toggleThankYouRoute}
            setThankYouRoute={props.setThankYouRoute}
            refreshThankYouRoute={props.refreshThankYouRoute}
            toggleBranchMembership={props.toggleBranchMembership}
            toggleFieldCollapsedState={props.toggleFieldCollapsedState}
            values={obj}
            uiState={objUiState}
          />
        );
      }

      case RENDER_TYPES.STEP: {
        stepMemo.index++; // eslint-disable-line no-param-reassign
        const followsBranchingStep = props.activeBranchingFieldDetails.length && stepMemo.index === props.activeBranchingFieldDetails[0].step + 1;
        return (
          <div key={fieldOrScreenId}>
            <StepBar
              addField={props.addField}
              fieldOrder={form.fieldOrder}
              index={index}
              onClickDelete={createOnClickDeleteStep(followsBranchingStep, index)}
              key={`step-${stepMemo.index}`}
              screens={form.screens}
              scrollToBottom={props.scrollToBottom}
              step={stepMemo.index}
              stepId={fieldOrScreenId}
            />
            {followsBranchingStep && showStepDeleteModal ? (
              <Modal>
                <Modal.Header
                  onCancel={stepOnCancelDelete}
                >
                  <h5 className="modal-title">Delete Step</h5>
                </Modal.Header>
                <Modal.Body>
                  {DELETE_STEP_MODAL_TEXT}
                </Modal.Body>
                <Modal.Footer>
                  <button type="button" id="delete-step-cancel-button" className="btn btn-secondary" onClick={stepOnCancelDelete}>Cancel</button>
                  <button type="button" id="delete-step-confirm-button" className="btn btn-info" onClick={createOnConfirmDeleteStep(fieldOrScreenId, index)}>Delete Step</button>
                </Modal.Footer>
              </Modal>
            ) : null}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const stepMemo = { index: 0 };
  const fieldErrors = invalidFormData.fields ? invalidFormData.fields : {};
  const fieldsAndScreens = (form.fieldOrder || []).map((id, index) => makeFieldOrScreen(id, index, stepMemo, fieldErrors));
  const formDetailsUISlice = {
    name: props.uiState.name,
    degreeOffering: props.uiState.degreeOffering,
    syntheticContentEntities: props.uiState.syntheticContentEntities,
    programOfStudy: props.uiState.programOfStudy,
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div onClick={onClickHandler}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <ErrorSummary
              errors={invalidFormData}
              form={form}
            />
          </div>
        </div>
        <FormDetails
          availableCredentialTypes={props.availableCredentialTypes}
          availablePrograms={availablePrograms}
          availableProgramsOfStudy={availableProgramsOfStudy}
          fieldValidityChanged={props.fieldValidityChanged}
          form={form}
          onConsentStyleChange={props.changeConsentStyle}
          onFieldValueChange={props.updateFormValue}
          onUIStateChange={props.updateUIState}
          supportsDegreeOffering={props.supportsDegreeOffering}
          uiState={formDetailsUISlice}
          updateDefaultDegreeOffering={props.updateDefaultDegreeOffering}
          updateProgramOfStudy={props.updateProgramOfStudy}
        />
        <div className="row">
          <div className="col-12">
            {fieldsAndScreens.map((F, idx) => (
              <div key={F.key}>
                {F}
                <FieldInserter
                  addField={props.addField}
                  index={idx + 1}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

FormBuilderView.propTypes = {
  activeBranches: PropTypes.object.isRequired,
  activeBranchingFieldDetails: PropTypes.array.isRequired,
  addField: PropTypes.func.isRequired,
  availableCredentialTypes: PropTypes.array.isRequired,
  availablePrograms: PropTypes.array,
  availableProgramsOfStudy: PropTypes.array,
  bulkToggleDropdownOptions: PropTypes.func.isRequired,
  changeConsentStyle: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  deleteStep: PropTypes.func.isRequired,
  fieldClicked: PropTypes.func.isRequired,
  fieldNameDropdownOpts: PropTypes.object.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  form: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    degreeOffering: PropTypes.string,
    programId: PropTypes.number,
    fields: PropTypes.object,
    fieldOrder: PropTypes.array,
    fieldConditionalityInfo: PropTypes.shape({
      answerToBranchMap: PropTypes.object,
      branchingFields: PropTypes.object,
      branchOrder: PropTypes.array,
      graph: PropTypes.object.isRequired,
    }).isRequired,
    thankYouRoutingFields: PropTypes.object.isRequired,
    groupingName: PropTypes.string.isRequired,
    groupingId: PropTypes.number,
    screens: PropTypes.array,
    syntheticContentEntities: PropTypes.shape({
      gdprSetting: PropTypes.string,
      disclaimers: PropTypes.object,
      inferredFields: PropTypes.object,
    }).isRequired,
    formMetadata: PropTypes.shape({
      isFormJSONEditable: PropTypes.bool,
      areAllFieldsOptional: PropTypes.bool,
      experimentalTrialAttributes: PropTypes.object,
      formAttributesConfig: PropTypes.object,
    }),
  }),
  invalidFormData: PropTypes.object,
  loadInProgress: PropTypes.bool,
  removeAllUserBranchMembership: PropTypes.func.isRequired,
  reorderDropdown: PropTypes.func.isRequired,
  reorderField: PropTypes.func.isRequired,
  scrollToBottom: PropTypes.func.isRequired,
  sfFieldData: PropTypes.object.isRequired,
  supportsDegreeOffering: PropTypes.bool.isRequired,
  toggleBranching: PropTypes.func.isRequired,
  toggleThankYouRoute: PropTypes.func.isRequired,
  setThankYouRoute: PropTypes.func.isRequired,
  refreshThankYouRoute: PropTypes.func.isRequired,
  toggleBranchMembership: PropTypes.func.isRequired,
  toggleFieldCollapsedState: PropTypes.func.isRequired,
  setFieldInferredState: PropTypes.func.isRequired,
  uiState: PropTypes.object.isRequired,
  updateDefaultDegreeOffering: PropTypes.func.isRequired,
  updateProgramOfStudy: PropTypes.func.isRequired,
  updateFieldName: PropTypes.func.isRequired,
  updateFormValue: PropTypes.func.isRequired,
  updateUIState: PropTypes.func.isRequired,
};

export default FormBuilderView;
